import React, { useEffect, useMemo, useRef, useState } from "react";
import NavBar from "frontend/Components/DesignSystem/NavBar";
import HeroAnimation from "frontend/Components/HeroAnimation/HeroAnimation";
import { useNavigate } from "react-router-dom";
import "../../../css/Adviser/Pages/AdviserHomePage.css";
import "../../../css/Adviser/Pages/AdviserEventPage.css";
import { ContactUs } from "frontend/Components/ContactForm/ContactUs";
import Footer from "frontend/Components/DesignSystem/Footer";
import AdviserValues from "frontend/Components/AdviserValues/AdviserValue";
import ButtonYellow from "frontend/Components/DesignSystem/Buttons/ButtonYellow";
import SeoHelmet from "frontend/Components/SEO/SeoHelmet";
import {
  SEO_PAGES,
  SEO_SITE,
  getLogoUrl,
  getPageUrl,
} from "frontend/Utils/seoConfig";
import ImageDirectory from "frontend/HelperFunctions/ImageDirectory";
import { getAdviserValues } from "frontend/Utils/GetAdviserValues";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import dayjs from "dayjs";

const PAGE_SIZE = 10; // show 10 events per page
const WINDOW_SIZE = 3; // show 3 page pills, sticky window

const EventsPage = () => {
  const navigationItems = [
    { label: "Home", navigateTo: "/Adviser" },
    { label: "Join Us", navigateTo: "/Adviser/JoinUs" },
    { label: "About Us", navigateTo: "/Adviser/AboutUs" },
    {
      label: "Contact Us",
      navigateTo: "/ContactUs",
      state: { isAdviser: true },
    },
  ];

  const navigate = useNavigate();
  const [values, setValues] = useState([]);

  // ----- events data (converted from your static JSX) -----
    const EVENTS = useMemo(
      () => [
        {
          title: "Financial Planning Peer Group",
          location: "London",
          date: dayjs("2026-10-08").toISOString(),
          description:
            "Our Financial Planning Peer Group brought together financial planning professionals at Schroders for a day of insight and discussion. The session covered key topics across the industry, with expert speakers sharing valuable perspectives, practical ideas and useful takeaways to support advisers in their work.",
          imageKey: "08-10-2026",
          tag: "Past Event",
        },
        {
          title: "Financial Planning Peer Group",
          location: "London",
          date: dayjs("2026-05-28").toISOString(),
          description:
            "We had a fantastic Financial Planning Peer Group on 28th May at Fidelity, bringing together a range of expert speakers for a full day of insight and discussion. Covering trusts, IHT, pension legislation, Fidelity funds, economic updates and Beanstalk, the event delivered practical ideas, valuable perspectives and actionable takeaways for attendees, followed by drinks afterwards.",
          imageKey: "28-05-2026",
          tag: "Past Event",
        },
        {
          title: "Practice Principal Masterclass",
          location: "London",
          date: dayjs("2026-05-15").toISOString(),
          description:
            "Our Practice Principal Masterclass brought together a range of expert speakers for a full day of insight and discussion. Covering employment law, client trends, wellbeing, economic updates and data, the Practice Principal Masterclass delivered practical ideas, valuable perspectives and actionable takeaways for attendees across the industry.",
          imageKey: "15-05-2026",
          tag: "Past Event",
        },
        {
          title: "Kent Peer Group",
          location: "Kent",
          date: dayjs("2026-04-30").toISOString(),
          description:
            "Heading over to Maidstone/Sevenoaks, we really looked forward to our third Peer Group of the year in Kent. We listened to speakers from Positive Lending, Scottish Widows, Handelsbanken, Paymentshield, and Guardian. It was a highly informative and collaborative session.",
          imageKey: "30-04-2026",
          tag: "Past Event",
        },
        {
          title: "South West Peer Group",
          location: "South West",
          date: dayjs("2026-04-23").toISOString(),
          description:
            "Following on from our London Peer Group, we had our second of the year, based in the South West. With confirmed speakers from Foundation, Positive Lending, Guardian, Scottish Widows, Handelsbanken and Paymentshield, we were all counting down the days until kick-off for this one. It was a valuable and well-received session with plenty of takeaways.",
          imageKey: "23-04-2026",
          tag: "Past Event",
        },
        {
          title: "London Peer Group",
          location: "London",
          date: dayjs("2026-04-16").toISOString(),
          description:
            "We kicked off 2026 Peer Groups and couldn’t wait for a year of collaboration, fresh ideas and continued growth across our network. At our London Peer Group this year, we were excited to announce that we had speakers from Positive Lending, Scottish Widows, Handelsbanken, Paymentshield, TML/Bluestone and Guardian. It was an engaging and insightful session that set a strong tone for the year ahead.",
          imageKey: "16-04-2026",
          tag: "Past Event",
        },
        {
          title: "2026 Rosemount Annual Conference",
          location: "London",
          date: dayjs("2026-01-16").toISOString(),
          description:
            "We hosted our biggest annual conference to date at the magnificent StoneX Stadium. During the day we had experts deliver fantastic talks and were joined by inspirational speakers including Dan Biddle from Six MHA. An excellent day of sharing insights, collaboration and strengthening connections across our network.",
          imageKey: "16-01-2026",
          tag: "Past Event",
        },
        {
          title: "Christmas Financial Planning Masterclass",
          location: "London",
          date: dayjs("2025-12-11").toISOString(),
          description:
            "At our Christmas Financial Planning Masterclass, we had excellent speakers from Scottish Widows. A fantastic day of learning, presentations and collaboration.",
          imageKey: "11-12-2025",
          tag: "Past Event",
        },
        {
          title: "South-West Peer Group",
          location: "South-West",
          date: dayjs("2025-10-09").toISOString(),
          description:
            "At our South-West Peer Group we had speakers from Halifax, Zurich, Hodge, Norton Home Loans and Paymentshield. Presentations ranged from protection propositions to the value of general insurance.",
          imageKey: "09-10-2025",
          tag: "Past Event",
        },
        {
          title: "Charity Ball",
          location: "Basingstoke",
          date: dayjs("2025-09-19").toISOString(),
          description:
            "Our 2025 charity ball was an excellent evening where we raised an incredible £33,026 for two excellent charities in Hazie Days and Stepping Stones DS.",
          imageKey: "Charity Ball - 2025",
          tag: "Past Event",
        },
        {
          title: "Kent Peer Group",
          location: "Kent",
          date: dayjs("2025-09-18").toISOString(),
          description:
            "At our Kent Peer Group we had speakers from Paymentshield, Loans Warehouse, Virgin Money, Halifax and Norton Home Loans. Presentations ranged from the current market overview to an insight into the value of insurance.",
          imageKey: "Kent-18-09-2025",
          tag: "Past Event",
        },
        {
          title: "London Peer Group",
          location: "London",
          date: dayjs("2025-07-17").toISOString(),
          description:
            " At our London Peer Group we had speakers from Hodge, Paragon, MetLife, Fleet Mortgages, HSBC Life, Vitality and Virgin Money. Presentations ranged from supporting the changing needs of today’s borrowers to opportunities in the buy to let marketplace.",
          imageKey: "17-07-2025",
          tag: "Past Event",
        },
        {
          title: "South-West Peer Group",
          location: "South-West",
          date: dayjs("2025-07-03").toISOString(),
          description:
            "At our South-West Peer Group we had speakers from Fleet Mortgages, MetLife, Santander, Vitality and Loans Warehouse. Presentations ranged from bridging and second charges to residential and BTL propositions.",
          imageKey: "03-07-2025",
          tag: "Past Event",
        },
        {
          title: "Kent Peer Group",
          location: "Kent",
          date: dayjs("2025-06-26").toISOString(),
          description:
            "At our Kent Peer Group we had speakers from Hodge Bank, MetLife, Fleet Mortgages, Vitality, Paragon Bank and HSBC. The presentations ranged from protection to the value of underwriting.",
          imageKey: "26-06-2025",
          tag: "Past Event",
        },
        {
          title: "London Peer Group",
          location: "London",
          date: dayjs("2025-05-22").toISOString(),
          description:
            "At our London Peer Group, we had speakers from NatWest, National Friendly, Together, Legal and General, Atom Bank and our own Hayleigh Kelly. Presentations ranged from updates & trends to income protection.",
          imageKey: "22-05-2025",
          tag: "Past Event",
        },
        {
          title: "Financial Planning Peer Group",
          location: "London",
          date: dayjs("2025-05-08").toISOString(),
          description:
            "At our Financial Planning Peer Group, we had speakers from 7IM, Canada Life, HSBC, Newton Investment Management and Vanguard. Presentations ranged from the current market outlook to the annuity market.",
          imageKey: "08-05-2025",
          tag: "Past Event",
        },
        {
          title: "Buy-to-Let Masterclass",
          location: "London",
          date: dayjs("2025-04-01").toISOString(),
          description:
            "At our Buy-to-Let masterclass we were joined by Roger Morris from CHL Mortgages for Intermediaries and John McCaffery from Alexander and Co Chartered Accountants to discuss the basics of owning a property through a limited company structure and advice on tax consideration.",
          imageKey: "01-04-2025",
          tag: "Past Event",
        },
        {
          title: "South-West Peer Group",
          location: "Bristol",
          date: dayjs("2025-03-20").toISOString(),
          description:
            "At our South-West Peer Group, we had speakers from National Friendly, Scottish Widow, Atom Bank, Virgin Money and our own Hayleigh Kelly join us. Presentations ranged from latest updates to current trends.",
          imageKey: "20-03-2025",
          tag: "Past Event",
        },
        {
          title: "Kent Peer Group",
          location: "Kent",
          date: dayjs("2025-03-13").toISOString(),
          description:
            " At our Kent Peer Group we had speakers from Perenna, National            Friendly, Together, Scottish Widow, Atom Bank and our own Hayliegh Kelly join us. Presentations ranged from latest updates to current markets.",
          imageKey: "17-03-2025", // your original src key
          tag: "Past Event",
        },
        {
          title: "2025 Rosemount Annual Conference",
          location: "London",
          date: dayjs("2025-01-17").toISOString(),
          description:
            "The Rosemount Annual Conference is the showcase event of the year. We kick off 2025 with a busy day of presentations, covering the economic outlook, separate Mortgage and Wealth interactive sessions, and talks from special guest speakers. We also recognise top performers from the previous year, and look ahead to our goals for 2025.",
          imageKey: "event-170125",
          tag: "Past Event",
        },
        {
          title: "Rosemount & Scottish Widows Financial Planning Session",
          location: "London",
          date: dayjs("2024-12-05").toISOString(),
          description:
            "A Rosemount event, in conjunction with platform partner Scottish Widows, run for our Financial Planners. The content included a full day of presentations on the current market post Autumn budget, Scottish Widows platform changes and a 3 course meal!",
          imageKey: "event-111025", // your original src key
          tag: "Past Event",
        },
        {
          title: "Financial Planning Peer Group",
          location: "London",
          date: dayjs("2024-10-11").toISOString(),
          description:
            "In October, we held a peer group for our Financial Planners, in Canary Wharf. The day included presentations from HSBC, Vanguard, 7IM and BNY Mellon, providing updates across investment funds and platform solutions. Additionally, Rosemount Head of Compliance, Iain Crowley, provided a presentation around sequence of returns risk in draw-down, including a general Rosemount compliance Q&A.",
          imageKey: "event-051225", // your original src key
          tag: "Past Event",
        },
      ],
      // .sort((a, b) => (a.date < b.date ? 1 : -1)), // newest first
      [],
    );
  
  // useEffect(() => {
  //   console.log("EVENTS:", EVENTS);
  //  }, [EVENTS]);

  // ----- pagination state -----
  const [page, setPage] = useState(1);
  const [windowStart, setWindowStart] = useState(1); // sticky window start for pills
  const topRef = useRef(null);
  const userPagedRef = useRef(false); // only scroll on user-initiated page changes

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(EVENTS.length / PAGE_SIZE)),
    [EVENTS.length]
  );

  const getWindowPages = (total, start, size = WINDOW_SIZE) => {
    if (total <= 0) return [];
    const maxStart = Math.max(1, total - size + 1);
    const safeStart = Math.min(Math.max(1, start), maxStart);
    const end = Math.min(total, safeStart + size - 1);
    return Array.from({ length: end - safeStart + 1 }, (_, i) => safeStart + i);
  };

  const updateWindow = (nextPage, total) => {
    const maxStart = Math.max(1, total - WINDOW_SIZE + 1);
    if (nextPage < windowStart) {
      setWindowStart(Math.max(1, nextPage));
    } else if (nextPage > windowStart + WINDOW_SIZE - 1) {
      setWindowStart(Math.min(maxStart, nextPage - WINDOW_SIZE + 1));
    }
  };

  const goPrev = () => {
    if (page === 1) return;
    userPagedRef.current = true;
    const next = page - 1;
    setPage(next);
    updateWindow(next, totalPages);
  };

  const goNext = () => {
    if (page === totalPages) return;
    userPagedRef.current = true;
    const next = page + 1;
    setPage(next);
    updateWindow(next, totalPages);
  };

  const goTo = (p) => {
    const next = Math.max(1, Math.min(totalPages, p));
    userPagedRef.current = true;
    setPage(next);
    updateWindow(next, totalPages);
  };

  const pageSlice = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return EVENTS.slice(start, start + PAGE_SIZE);
  }, [EVENTS, page]);

  // keep window/page valid if totals change (future-proof)
  useEffect(() => {
    const maxStart = Math.max(1, totalPages - WINDOW_SIZE + 1);
    setWindowStart((prev) => Math.min(prev, maxStart));
    setPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  // smooth scroll to top of list ONLY when user paged
  useEffect(() => {
    if (!userPagedRef.current) return;
    if (topRef.current) {
      const headerOffset = 88; // adjust to your sticky NavBar height
      const y =
        topRef.current.getBoundingClientRect().top +
        window.pageYOffset -
        headerOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
    userPagedRef.current = false;
  }, [page]);

  // adviser values
  useEffect(() => {
    const fetchValues = async () => {
      const data = await getAdviserValues();
      setValues(data || []);
    };
    fetchValues();
  }, []);

  const pageUrl = getPageUrl(SEO_PAGES.events.path);
  const logoUrl = getLogoUrl();

  const eventsStructuredData = {
    "@type": "ItemList",
    name: "Rosemount Adviser Events",
    description: SEO_PAGES.events.description,
    url: pageUrl,
    itemListElement: EVENTS.map((event, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "Event",
        name: event.title,
        description: event.description,
        startDate: event.date,
        eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
        eventStatus: "https://schema.org/EventScheduled",
        image: [logoUrl],
        url: pageUrl,
        location: {
          "@type": "Place",
          name: event.location,
          address: {
            "@type": "PostalAddress",
            addressLocality: event.location,
            addressCountry: "GB",
          },
        },
        organizer: {
          "@id": `${SEO_SITE.siteUrl}/#organization`,
        },
      },
    })),
  };

  return (
    <div className="Adviser-Home-Page">
      <SeoHelmet
        page={SEO_PAGES.events}
        breadcrumbs={[
          {
            name: "Home",
            url: getPageUrl("/"),
          },
          {
            name: "Advisers",
            url: getPageUrl("/Adviser"),
          },
          {
            name: "Events",
            url: getPageUrl("/Adviser/Events"),
          },
        ]}
        extraSchemas={[eventsStructuredData]}
      />

      <NavBar navigationItems={navigationItems} />

      {/* anchor for scroll-to-top */}
      <div ref={topRef} />

      <HeroAnimation
        imgSrc={ImageDirectory["events"]}
        isAdviser={true}
        altText="Events room with multiple desks, people in business attire looking at the speaker with a presentation on the board."
      >
        <h1>Events</h1>
      </HeroAnimation>

      {/* Render paginated events */}
      {pageSlice.map((ev, idx) => {
        const globalIndex = (page - 1) * PAGE_SIZE + idx;
        return (
          <div
            key={globalIndex}
            className={`Events-Container ${globalIndex % 2 === 0 ? "" : ""}`}
          >
            <div className="Events-Container-Content">
              <h3>{ev.title}</h3>
              <h4>
                {ev.location}{" "}
                {ev.date ? dayjs(ev.date).format("DD-MM-YYYY") : ""}
              </h4>
              <div className="Content-Large">{ev.description}</div>
            </div>
            <div className="Image-Container">
              <img
                loading="eager"
                fetchPriority="high"
                src={ImageDirectory[ev.imageKey]}
                alt={`${ev.title} adviser event hosted by Rosemount in ${ev.location}`}
              />
              <div className="Image-Text-Overlay">
                <h3>{ev.tag}</h3>
              </div>
            </div>
          </div>
        );
      })}

      {/* Pagination controls (match your other pages) */}
      {EVENTS.length > 0 && (
        <div className="PaginationControls">
          <button
            type="button"
            className="pagination-icon-btn"
            onClick={goPrev}
            disabled={page === 1}
            aria-label="Previous page"
          >
            <ChevronLeftIcon />
          </button>

          <div className="pagination-pages">
            {getWindowPages(totalPages, windowStart).map((p) => (
              <button
                key={p}
                type="button"
                className={`pagination-page-btn ${
                  p === page ? "is-active" : ""
                }`}
                onClick={() => goTo(p)}
                aria-current={p === page ? "page" : undefined}
                aria-label={`Page ${p}`}
              >
                <div className="Content-SemiBold">{p}</div>
              </button>
            ))}
          </div>

          <button
            type="button"
            className="pagination-icon-btn"
            onClick={goNext}
            disabled={page === totalPages}
            aria-label="Next page"
          >
            <ChevronRightIcon />
          </button>
        </div>
      )}

      <AdviserValues
        values={values}
        isAdviser={true}
        title="Your Partner in Success"
      >
        <ButtonYellow
          buttonText="Join Us"
          onClick={() => navigate("/Adviser/JoinUs")}
        />
      </AdviserValues>

      <ContactUs isAdviser={true} />
      <Footer isAdviser={true} />
    </div>
  );
};

export default EventsPage;
